import type { ServiceDetail } from "@/types/service";

/** Structural subset shared by staff and public service projections. */
type AddonSelectionService = Pick<ServiceDetail, "selectionMax"> & {
  addons?: {
    id: string;
    addonService?: { allowMultipleBookings?: boolean };
  }[];
};

/**
 * Add-on selections are a multiset, not a set: the same add-on can be picked
 * more than once ("2 × Small area") only when that add-on service allows multiple
 * bookings, and each pick becomes its own segment on
 * the timeline. The selection is carried as a plain string[] of add-on ids with
 * repeats, which is what `updateServiceAddons` already expects — it creates one
 * segment per entry.
 *
 * `selectionMin` / `selectionMax` count picks, not distinct add-ons, so picking
 * one add-on twice fills a "pick 2" requirement.
 */

/** How many times this add-on is currently picked. */
export function countAddon(addonIds: string[], addonId: string): number {
  return addonIds.filter((id) => id === addonId).length;
}

/**
 * Picks left before `selectionMax` is reached, ignoring the add-on's own picks
 * so it can be raised to fill the remaining room.
 */
export function remainingCapacity(
  service: Pick<ServiceDetail, "selectionMax">,
  addonIds: string[],
  excludingAddonId?: string,
): number {
  const selectionMax = service.selectionMax ?? null;
  if (selectionMax === null) return Number.POSITIVE_INFINITY;

  const taken =
    excludingAddonId === undefined
      ? addonIds.length
      : addonIds.filter((id) => id !== excludingAddonId).length;

  return Math.max(0, selectionMax - taken);
}

/**
 * Sets how many times an add-on is picked, clamped to `selectionMax` and the
 * add-on service's Allow Multiple Bookings setting (never the parent's).
 *
 * Repeats stay together at the position of the first existing pick so the
 * generated segments keep their place on the timeline; a newly picked add-on
 * goes on the end. Returns the same array reference when nothing changes.
 */
export function setAddonQuantity(
  service: AddonSelectionService,
  currentAddonIds: string[],
  addonId: string,
  quantity: number,
): string[] {
  const addon = service.addons?.find((item) => item.id === addonId);
  const repeatLimit =
    addon?.addonService?.allowMultipleBookings === true ? Infinity : 1;
  const capacity = Math.min(
    remainingCapacity(service, currentAddonIds, addonId),
    repeatLimit,
  );
  const next = Math.max(0, Math.min(Math.floor(quantity), capacity));

  if (next === countAddon(currentAddonIds, addonId)) return currentAddonIds;

  const repeats = Array<string>(next).fill(addonId);
  const result: string[] = [];
  let placed = false;

  for (const id of currentAddonIds) {
    if (id === addonId) {
      if (!placed) {
        result.push(...repeats);
        placed = true;
      }
      continue;
    }
    result.push(id);
  }
  if (!placed) result.push(...repeats);

  return result;
}

/**
 * Selects an add-on that is not picked yet, or clears it entirely if it is.
 *
 * `selectionMax === 1` is a radio: picking one replaces whatever was there,
 * rather than being refused for being at the limit.
 */
export function toggleAddonId(
  service: AddonSelectionService,
  currentAddonIds: string[],
  addonId: string,
): string[] {
  if (countAddon(currentAddonIds, addonId) > 0) {
    return currentAddonIds.filter((id) => id !== addonId);
  }
  if ((service.selectionMax ?? null) === 1) return [addonId];

  return setAddonQuantity(service, currentAddonIds, addonId, 1);
}
